import {
  cellCenter,
  dropfrontCatalog,
  type DropfrontCardDef,
  type DropfrontCardId,
  type DropfrontTargetKind,
  type DropPreviewKind,
  type EnemyRoleId,
  type StructureKind,
} from "../../data/dropfront-catalog";
import { bfsNextStep, cellKey, chebyshev } from "../../sim";
import { resolveRigConfig } from "../../view/unit-rig";
import {
  attackTravelSec,
  resolveAttackFx,
  type AttackDelivery,
} from "./view/attack-vfx";
import {
  DfDropReason,
  DfEvent,
  DfPhase,
  type Phase,
  type SimEvent,
} from "./dropfront-events";

export type { Phase, SimEvent } from "./dropfront-events";
export { DfDropReason, DfEvent, DfPhase } from "./dropfront-events";

export type AttackTargetKind = "enemy" | "structure" | "ally" | "barrier";

/** In-flight / wind-up attack; damage applies when remain <= 0. */
export type PendingAttack = {
  id: string;
  fromId: string;
  toId: string;
  role: string;
  delivery: AttackDelivery;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  remain: number;
  duration: number;
  damage: number;
  targetKind: AttackTargetKind;
};

/** HoMM-style ally stack on one cell (same cardId). */
export type AllyPiece = {
  id: string;
  cardId: DropfrontCardId;
  col: number;
  row: number;
  fromCol: number;
  fromRow: number;
  /** 0 = at from, 1 = arrived at col/row (idle). */
  moveT: number;
  stackCount: number;
  hp: number;
  maxHpPerUnit: number;
  landing: number;
  fireCd: number;
  synergy: boolean;
  aimFacing: 1 | -1;
  /** Continuous aim (radians); SSOT for view facingDir / attack FX. */
  aimAngle: number;
  /** Remaining attack lock (sec); blocks movement while > 0. */
  attackRemain: number;
};

/** Grid enemy squad (same role stacks). */
export type EnemyAgent = {
  id: string;
  col: number;
  row: number;
  fromCol: number;
  fromRow: number;
  moveT: number;
  role: EnemyRoleId;
  stackCount: number;
  hp: number;
  maxHpPerUnit: number;
  dps: number;
  engaging: boolean;
  facing: 1 | -1;
  aimAngle: number;
  attackRemain: number;
  fireCd: number;
};

export type StructureToken = {
  id: string;
  kind: StructureKind;
  col: number;
  row: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  active: boolean;
  fireCd: number;
};

export type BarrierToken = {
  id: string;
  col: number;
  row: number;
  hp: number;
  maxHp: number;
};

export type DropfrontState = {
  time: number;
  phase: Phase;
  supply: number;
  selectedCard: DropfrontCardId | null;
  allies: AllyPiece[];
  enemies: EnemyAgent[];
  structures: StructureToken[];
  barriers: BarrierToken[];
  attacks: PendingAttack[];
  events: SimEvent[];
  dropsUsed: Partial<Record<DropfrontCardId, number>>;
  nextId: number;
  nestSpawnCd: number;
  hqPulseCd: number;
};

export type HudSnapshot = {
  supply: number;
  hqHp: number;
  hqMax: number;
  phase: Phase;
  selectedCard: DropfrontCardId | null;
  cardAfford: Record<DropfrontCardId, boolean>;
};

export type DropResult = {
  ok: boolean;
  next: DropfrontState;
  reason?: DfDropReason;
};

const ORTHO = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;

function inBounds(col: number, row: number): boolean {
  const { cols, rows } = dropfrontCatalog.level;
  return col >= 0 && row >= 0 && col < cols && row < rows;
}

function isHazard(col: number, row: number): boolean {
  return dropfrontCatalog.level.hazardCells.some(
    (c) => c.col === col && c.row === row,
  );
}

function easeMoveT(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

type GridMover = {
  fromCol: number;
  fromRow: number;
  col: number;
  row: number;
  moveT: number;
};

/** Interpolated world position (cell units) for combat + presentation. */
export function unitWorld(u: GridMover): { x: number; y: number } {
  const t = easeMoveT(u.moveT);
  const a = cellCenter(u.fromCol, u.fromRow);
  const b = cellCenter(u.col, u.row);
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function worldDist(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** ~1 cell ortho adjacency in cell-center space. */
const MELEE_RANGE = 1.15;

function snapIdle(u: GridMover): void {
  u.fromCol = u.col;
  u.fromRow = u.row;
  u.moveT = 1;
}

function beginStep(u: GridMover, toCol: number, toRow: number): void {
  u.fromCol = u.col;
  u.fromRow = u.row;
  u.col = toCol;
  u.row = toRow;
  u.moveT = 0;
}

function isMoving(u: GridMover): boolean {
  return u.moveT < 1;
}

export function aimAngleToward(
  from: { x: number; y: number },
  to: { x: number; y: number },
): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

export function facingFromAim(aim: number): 1 | -1 {
  return Math.cos(aim) >= 0 ? 1 : -1;
}

function setAim(
  u: { aimAngle: number; aimFacing?: 1 | -1; facing?: 1 | -1 },
  aim: number,
): void {
  u.aimAngle = aim;
  const f = facingFromAim(aim);
  if ("aimFacing" in u) u.aimFacing = f;
  if ("facing" in u) u.facing = f;
}

function swingSecFor(role: string): number {
  return resolveRigConfig(dropfrontCatalog.view.rig, role).swingSec;
}

function attackFxFor(role: string) {
  return resolveAttackFx(dropfrontCatalog.view.attackFx, role);
}

function isAttacking(u: { attackRemain: number }): boolean {
  return u.attackRemain > 0;
}

function enqueueAttack(
  state: DropfrontState,
  events: SimEvent[],
  opts: {
    fromId: string;
    toId: string;
    role: string;
    from: { x: number; y: number };
    to: { x: number; y: number };
    damage: number;
    targetKind: AttackTargetKind;
    swingSec: number;
  },
): { attack: PendingAttack; lockSec: number } {
  const fx = attackFxFor(opts.role);
  const dist = Math.hypot(opts.to.x - opts.from.x, opts.to.y - opts.from.y);
  const travel = attackTravelSec(fx, dist, opts.swingSec);
  const lockSec = Math.max(opts.swingSec, travel);
  const id = `atk_${state.nextId++}`;
  const attack: PendingAttack = {
    id,
    fromId: opts.fromId,
    toId: opts.toId,
    role: opts.role,
    delivery: fx.delivery,
    x0: opts.from.x,
    y0: opts.from.y,
    x1: opts.to.x,
    y1: opts.to.y,
    remain: travel,
    duration: travel,
    damage: opts.damage,
    targetKind: opts.targetKind,
  };
  state.attacks.push(attack);
  events.push({
    type: DfEvent.Shot,
    fromId: opts.fromId,
    toId: opts.toId,
    x0: opts.from.x,
    y0: opts.from.y,
    x1: opts.to.x,
    y1: opts.to.y,
    role: opts.role,
    delivery: fx.delivery,
    attackId: id,
  });
  return { attack, lockSec };
}

function stackCountFromHp(hp: number, maxHpPerUnit: number): number {
  if (hp <= 0 || maxHpPerUnit <= 0) return 0;
  return Math.floor((hp - 1) / maxHpPerUnit) + 1;
}

function allyAt(
  state: DropfrontState,
  col: number,
  row: number,
): AllyPiece | undefined {
  return state.allies.find((a) => a.hp > 0 && a.col === col && a.row === row);
}

function enemyAt(
  state: DropfrontState,
  col: number,
  row: number,
): EnemyAgent | undefined {
  return state.enemies.find((e) => e.hp > 0 && e.col === col && e.row === row);
}

function hardBlocked(
  state: DropfrontState,
  col: number,
  row: number,
  opts?: { ignoreAllyId?: string; ignoreEnemyId?: string; allowBeacon?: boolean },
): boolean {
  if (!inBounds(col, row)) return true;
  if (barrierAt(state, col, row)) {
    return true;
  }
  for (const s of state.structures) {
    if (!s.alive) continue;
    if (s.col !== col || s.row !== row) continue;
    if (opts?.allowBeacon && s.kind === "beacon") continue;
    return true;
  }
  const ally = allyAt(state, col, row);
  if (ally && ally.id !== opts?.ignoreAllyId) return true;
  const en = enemyAt(state, col, row);
  if (en && en.id !== opts?.ignoreEnemyId) return true;
  return false;
}

function barrierAt(
  state: DropfrontState,
  col: number,
  row: number,
): BarrierToken | undefined {
  return state.barriers.find((b) => b.hp > 0 && b.col === col && b.row === row);
}

/**
 * Nearest free ortho neighbor of a blocked structure/barrier cell.
 * Same-card merge cells count as approachable. Returns the from cell when
 * the unit is already standing on a valid approach tile.
 */
function bestApproachCell(
  state: DropfrontState,
  targetCol: number,
  targetRow: number,
  fromCol: number,
  fromRow: number,
  opts?: { ignoreAllyId?: string; mergeAllyCard?: DropfrontCardId },
): { col: number; row: number } | null {
  let best: { col: number; row: number } | null = null;
  let bestD = Infinity;
  for (const [dc, dr] of ORTHO) {
    const nc = targetCol + dc;
    const nr = targetRow + dr;
    if (!inBounds(nc, nr)) continue;
    if (nc === fromCol && nr === fromRow) {
      return { col: nc, row: nr };
    }
    const mergeSame =
      opts?.mergeAllyCard != null
        ? state.allies.find(
            (o) =>
              o.hp > 0 &&
              o.id !== opts.ignoreAllyId &&
              o.col === nc &&
              o.row === nr &&
              o.cardId === opts.mergeAllyCard,
          )
        : undefined;
    if (
      !mergeSame &&
      hardBlocked(state, nc, nr, { ignoreAllyId: opts?.ignoreAllyId })
    ) {
      continue;
    }
    const d = chebyshev(fromCol, fromRow, nc, nr);
    if (d < bestD) {
      bestD = d;
      best = { col: nc, row: nr };
    }
  }
  return best;
}

/** If goal sits on a hard-blocked structure/barrier, rewrite to a stand-off cell. */
function resolveStandOffGoal(
  state: DropfrontState,
  fromCol: number,
  fromRow: number,
  goalCol: number,
  goalRow: number,
  opts?: { ignoreAllyId?: string; mergeAllyCard?: DropfrontCardId },
): { col: number; row: number } {
  const struct = state.structures.find(
    (s) => s.alive && s.col === goalCol && s.row === goalRow,
  );
  const barrier = barrierAt(state, goalCol, goalRow);
  if (!struct && !barrier) {
    return { col: goalCol, row: goalRow };
  }
  const approach = bestApproachCell(
    state,
    goalCol,
    goalRow,
    fromCol,
    fromRow,
    opts,
  );
  return approach ?? { col: goalCol, row: goalRow };
}

/** Drop blocked unless empty or same-card stack merge. */
export function isOccupied(state: DropfrontState, col: number, row: number): boolean {
  if (!inBounds(col, row)) return true;
  if (barrierAt(state, col, row)) {
    return true;
  }
  if (state.structures.some((s) => s.alive && s.col === col && s.row === row)) {
    return true;
  }
  if (enemyAt(state, col, row)) return true;
  if (allyAt(state, col, row)) return true;
  return false;
}

export function isBlockedDrop(
  state: DropfrontState,
  col: number,
  row: number,
  cardId?: DropfrontCardId | null,
): boolean {
  if (!inBounds(col, row)) return true;
  const card = cardId ?? state.selectedCard;
  const ally = allyAt(state, col, row);
  if (ally && card && ally.cardId === card) return false; // stack merge
  return isOccupied(state, col, row);
}

export function isHazardCell(_state: DropfrontState, col: number, row: number): boolean {
  return isHazard(col, row);
}

function computeSynergyIds(state: DropfrontState): Set<string> {
  const byCell = new Map<string, AllyPiece>();
  for (const a of state.allies) {
    if (a.hp <= 0 || a.landing > 0) continue;
    byCell.set(cellKey(a.col, a.row), a);
  }
  const syn = new Set<string>();
  for (const a of byCell.values()) {
    for (const [dc, dr] of ORTHO) {
      if (byCell.has(cellKey(a.col + dc, a.row + dr))) {
        syn.add(a.id);
        break;
      }
    }
  }
  return syn;
}

function refreshSynergy(state: DropfrontState): void {
  const syn = computeSynergyIds(state);
  for (const a of state.allies) {
    a.synergy = a.hp > 0 && a.landing <= 0 && syn.has(a.id);
  }
}

export function previewDropCell(
  state: DropfrontState,
  col: number,
  row: number,
): DropPreviewKind {
  if (!inBounds(col, row)) return "blocked";
  const cardId = state.selectedCard;
  const ally = allyAt(state, col, row);
  if (ally && cardId && ally.cardId === cardId) return "stack";
  if (isOccupied(state, col, row)) return "blocked";
  if (isHazard(col, row)) return "danger";
  const ghost: AllyPiece = {
    id: "__ghost__",
    cardId: cardId ?? "assault",
    col,
    row,
    fromCol: col,
    fromRow: row,
    moveT: 1,
    stackCount: 1,
    hp: 1,
    maxHpPerUnit: 1,
    landing: 0,
    fireCd: 0,
    synergy: false,
    aimFacing: 1,
    aimAngle: 0,
    attackRemain: 0,
  };
  const syn = computeSynergyIds({ ...state, allies: [...state.allies, ghost] });
  return syn.has(ghost.id) ? "synergy" : "solo";
}

/** BFS next ortho step toward goal; null if blocked/stuck. */
function nextStepToward(
  state: DropfrontState,
  fromCol: number,
  fromRow: number,
  goalCol: number,
  goalRow: number,
  opts: {
    ignoreAllyId?: string;
    ignoreEnemyId?: string;
    mergeAllyCard?: DropfrontCardId;
    mergeEnemyRole?: EnemyRoleId;
    /** Allow stepping onto an enemy occupying the goal (ally charge). */
    chargeEnemyGoal?: boolean;
  },
): { col: number; row: number } | null {
  const standOff = resolveStandOffGoal(
    state,
    fromCol,
    fromRow,
    goalCol,
    goalRow,
    {
      ignoreAllyId: opts.ignoreAllyId,
      mergeAllyCard: opts.mergeAllyCard,
    },
  );
  goalCol = standOff.col;
  goalRow = standOff.row;
  if (fromCol === goalCol && fromRow === goalRow) return null;
  const passable = (col: number, row: number): boolean => {
    if (!inBounds(col, row)) return false;
    // Ally charge: path through / onto enemy cells
    if (opts.chargeEnemyGoal && enemyAt(state, col, row)) return true;
    if (col === goalCol && row === goalRow) {
      if (opts.mergeAllyCard) {
        const a = allyAt(state, col, row);
        if (a && a.cardId === opts.mergeAllyCard) return true;
      }
      if (opts.mergeEnemyRole) {
        const e = enemyAt(state, col, row);
        if (e && e.role === opts.mergeEnemyRole) return true;
      }
      if (hardBlocked(state, col, row, opts)) return false;
      return true;
    }
    if (opts.mergeAllyCard) {
      const a = allyAt(state, col, row);
      if (a && a.id !== opts.ignoreAllyId && a.cardId === opts.mergeAllyCard) {
        return true;
      }
    }
    if (opts.mergeEnemyRole) {
      const e = enemyAt(state, col, row);
      if (e && e.id !== opts.ignoreEnemyId && e.role === opts.mergeEnemyRole) {
        return true;
      }
    }
    return !hardBlocked(state, col, row, opts);
  };

  const step = bfsNextStep(fromCol, fromRow, goalCol, goalRow, passable);
  if (!step) return null;
  const { col: sc, row: sr } = step;
  if (sc === goalCol && sr === goalRow) {
    if (opts.chargeEnemyGoal && enemyAt(state, sc, sr)) {
      return { col: sc, row: sr };
    }
    if (hardBlocked(state, sc, sr, opts)) return null;
  }
  // Intermediate charge step onto enemy
  if (opts.chargeEnemyGoal && enemyAt(state, sc, sr)) {
    return { col: sc, row: sr };
  }
  return { col: sc, row: sr };
}

function mergeAllyInto(target: AllyPiece, addCount: number, addHp: number): void {
  const max = dropfrontCatalog.combat.maxStack;
  const room = Math.max(0, max - target.stackCount);
  const n = Math.min(addCount, room);
  if (n <= 0) return;
  target.stackCount += n;
  target.hp += addHp * (n / addCount);
}

function applyAllyDamage(
  state: DropfrontState,
  ally: AllyPiece,
  raw: number,
  events: SimEvent[],
  meta?: {
    role?: string;
    attackId?: string;
    x0?: number;
    y0?: number;
    x1?: number;
    y1?: number;
  },
): void {
  let dmg = raw;
  if (hasAdjacentAssaultCover(state, ally)) {
    dmg *= dropfrontCatalog.combat.coverDamageMul;
  } else if (hasAdjacentBarrierCover(state, ally)) {
    dmg *= dropfrontCatalog.combat.barrierCoverMul;
  }
  dmg = Math.max(1, Math.round(dmg));
  ally.hp -= dmg;
  ally.stackCount = stackCountFromHp(ally.hp, ally.maxHpPerUnit);
  const c = unitWorld(ally);
  events.push({
    type: DfEvent.Hit,
    id: ally.id,
    toId: ally.id,
    x: c.x,
    y: c.y,
    damage: dmg,
    role: meta?.role,
    targetKind: "ally",
    attackId: meta?.attackId,
    x0: meta?.x0,
    y0: meta?.y0,
    x1: meta?.x1,
    y1: meta?.y1,
  });
  if (ally.hp <= 0 || ally.stackCount <= 0) {
    ally.hp = 0;
    ally.stackCount = 0;
    events.push({
      type: DfEvent.Destroy,
      id: ally.id,
      x: c.x,
      y: c.y,
      kind: "ally",
    });
  }
}

function applyEnemyDamage(
  state: DropfrontState,
  enemy: EnemyAgent,
  raw: number,
  events: SimEvent[],
  meta?: {
    role?: string;
    attackId?: string;
    x0?: number;
    y0?: number;
    x1?: number;
    y1?: number;
  },
): void {
  const mul =
    countSynergyAttackersOnEnemy(state, enemy) >= 2
      ? dropfrontCatalog.combat.crossfireMul
      : 1;
  const dmg = Math.max(1, Math.round(raw * mul));
  enemy.hp -= dmg;
  enemy.stackCount = stackCountFromHp(enemy.hp, enemy.maxHpPerUnit);
  const c = unitWorld(enemy);
  events.push({
    type: DfEvent.Hit,
    id: enemy.id,
    toId: enemy.id,
    x: c.x,
    y: c.y,
    damage: dmg,
    role: meta?.role,
    targetKind: "enemy",
    attackId: meta?.attackId,
    x0: meta?.x0,
    y0: meta?.y0,
    x1: meta?.x1,
    y1: meta?.y1,
  });
  if (enemy.hp <= 0 || enemy.stackCount <= 0) {
    enemy.hp = 0;
    enemy.stackCount = 0;
    events.push({
      type: DfEvent.Destroy,
      id: enemy.id,
      x: c.x,
      y: c.y,
      kind: "swarm",
    });
    events.push({ type: DfEvent.Toast, messageKey: "df.msg.swarmDown" });
  }
}

function spawnEnemy(
  state: DropfrontState,
  role: EnemyRoleId,
  col: number,
  row: number,
  id?: string,
): EnemyAgent {
  const def = dropfrontCatalog.combat.enemyRoles[role];
  return {
    id: id ?? `enemy_${state.nextId++}`,
    col,
    row,
    fromCol: col,
    fromRow: row,
    moveT: 1,
    role,
    stackCount: 1,
    hp: def.hp,
    maxHpPerUnit: def.hp,
    dps: def.dps,
    engaging: false,
    facing: -1,
    aimAngle: Math.PI,
    attackRemain: 0,
    fireCd: 0,
  };
}

export function createDropfrontState(): DropfrontState {
  const { level, combat } = dropfrontCatalog;
  const structures: StructureToken[] = level.structures.map((s) => ({
    id: s.id,
    kind: s.kind,
    col: s.col,
    row: s.row,
    hp: s.hp,
    maxHp: s.hp,
    alive: true,
    active: false,
    fireCd: 0,
  }));
  const enemies: EnemyAgent[] = level.enemies.map((e) => {
    const def = combat.enemyRoles[e.role];
    return {
      id: e.id,
      col: e.col,
      row: e.row,
      fromCol: e.col,
      fromRow: e.row,
      moveT: 1,
      role: e.role,
      stackCount: 1,
      hp: def.hp,
      maxHpPerUnit: def.hp,
      dps: def.dps,
      engaging: false,
      facing: -1,
      aimAngle: Math.PI,
      attackRemain: 0,
      fireCd: 0,
    };
  });
  return {
    time: 0,
    phase: DfPhase.Playing,
    supply: level.initialSupply,
    selectedCard: null,
    allies: [],
    enemies,
    structures,
    barriers: [],
    attacks: [],
    events: [],
    dropsUsed: {},
    nextId: 1,
    nestSpawnCd: level.nestSpawnInterval * 0.5,
    hqPulseCd: combat.hqPulseInterval,
  };
}

export function restartDropfront(): DropfrontState {
  return createDropfrontState();
}

export function selectCard(
  state: DropfrontState,
  cardId: DropfrontCardId | null,
): DropfrontState {
  if (cardId !== null && state.selectedCard === cardId) {
    state.selectedCard = null;
  } else {
    state.selectedCard = cardId;
  }
  state.events = [];
  return state;
}

export function setPaused(state: DropfrontState, paused: boolean): DropfrontState {
  if (state.phase === DfPhase.Won || state.phase === DfPhase.Lost) return state;
  state.phase = paused ? DfPhase.Paused : DfPhase.Playing;
  state.events = [];
  return state;
}

export function tryDrop(state: DropfrontState, col: number, row: number): DropResult {
  state.events = [];
  if (state.phase !== DfPhase.Playing) {
    return { ok: false, next: state, reason: DfDropReason.Blocked };
  }
  const cardId = state.selectedCard;
  if (!cardId) {
    state.events.push({
      type: DfEvent.DropFail,
      reason: DfDropReason.NoCard,
      messageKey: "df.fail.noCard",
    });
    return { ok: false, next: state, reason: DfDropReason.NoCard };
  }
  const card = dropfrontCatalog.cardById[cardId];
  if (state.supply < card.cost) {
    state.events.push({
      type: DfEvent.DropFail,
      reason: DfDropReason.Insufficient,
      messageKey: "df.fail.supply",
      amount: card.cost,
    });
    return { ok: false, next: state, reason: DfDropReason.Insufficient };
  }
  if (!inBounds(col, row)) {
    state.events.push({
      type: DfEvent.DropFail,
      reason: DfDropReason.Oob,
      messageKey: "df.fail.blocked",
    });
    return { ok: false, next: state, reason: DfDropReason.Oob };
  }

  const existing = allyAt(state, col, row);
  if (existing && existing.cardId === cardId) {
    if (existing.stackCount >= dropfrontCatalog.combat.maxStack) {
      state.events.push({
        type: DfEvent.DropFail,
        reason: DfDropReason.Blocked,
        messageKey: "df.fail.blocked",
      });
      return { ok: false, next: state, reason: DfDropReason.Blocked };
    }
    state.supply -= card.cost;
    state.dropsUsed[cardId] = (state.dropsUsed[cardId] ?? 0) + 1;
    existing.stackCount += 1;
    existing.hp += card.hp;
    const c = cellCenter(col, row);
    state.events.push({ type: DfEvent.Land, id: existing.id, x: c.x, y: c.y });
    return { ok: true, next: state };
  }

  if (isOccupied(state, col, row)) {
    state.events.push({
      type: DfEvent.DropFail,
      reason: DfDropReason.Blocked,
      messageKey: "df.fail.blocked",
    });
    return { ok: false, next: state, reason: DfDropReason.Blocked };
  }

  state.supply -= card.cost;
  state.dropsUsed[cardId] = (state.dropsUsed[cardId] ?? 0) + 1;
  const c = cellCenter(col, row);

  if (card.role === "barrier") {
    refreshSynergy(state);
    const wouldSyn = previewDropCell(state, col, row) === "synergy";
    const maxHp =
      (card.barrierHp ?? card.hp) +
      (wouldSyn ? (card.barrierSynergyHpBonus ?? 0) : 0);
    const id = `barrier_${state.nextId++}`;
    state.barriers.push({ id, col, row, hp: maxHp, maxHp });
    state.events.push({
      type: DfEvent.PodFall,
      id,
      x: c.x,
      y: c.y - dropfrontCatalog.view.layout.landingArcCells,
      cardId,
    });
    state.events.push({ type: DfEvent.Land, id, x: c.x, y: c.y });
    return { ok: true, next: state };
  }

  const id = `ally_${state.nextId++}`;
  const piece: AllyPiece = {
    id,
    cardId,
    col,
    row,
    fromCol: col,
    fromRow: row,
    moveT: 1,
    stackCount: 1,
    hp: card.hp,
    maxHpPerUnit: card.hp,
    landing: dropfrontCatalog.level.dropSeconds,
    fireCd: 0.1,
    synergy: false,
    aimFacing: 1,
    aimAngle: 0,
    attackRemain: 0,
  };
  state.allies.push(piece);
  state.events.push({
    type: DfEvent.PodFall,
    id,
    x: c.x,
    y: c.y - dropfrontCatalog.view.layout.landingArcCells,
    cardId,
  });
  return { ok: true, next: state };
}

type TargetScoreKind = DropfrontTargetKind | "barrier";

type TargetRef =
  | { kind: "enemy"; unit: EnemyAgent; scoreKind: TargetScoreKind }
  | { kind: "structure"; unit: StructureToken; scoreKind: TargetScoreKind }
  | { kind: "barrier"; unit: BarrierToken; scoreKind: "barrier" };

function pickTarget(
  card: DropfrontCardDef,
  from: AllyPiece,
  state: DropfrontState,
): TargetRef | null {
  const fromW = unitWorld(from);
  const candidates: TargetRef[] = [];
  for (const e of state.enemies) {
    if (e.hp <= 0) continue;
    if (allyCanEngageEnemy(from, e, card.range)) {
      candidates.push({ kind: "enemy", unit: e, scoreKind: "swarm" });
    }
  }
  for (const s of state.structures) {
    if (!s.alive) continue;
    if (allyCanEngageStructure(from, s, card.range)) {
      candidates.push({ kind: "structure", unit: s, scoreKind: s.kind });
    }
  }
  for (const b of state.barriers) {
    if (b.hp <= 0) continue;
    if (allyCanEngageBarrier(from, b, card.range)) {
      candidates.push({ kind: "barrier", unit: b, scoreKind: "barrier" });
    }
  }
  let best: TargetRef | null = null;
  let bestPri = 999;
  let bestDist = Infinity;
  for (const t of candidates) {
    const tw =
      t.kind === "enemy"
        ? unitWorld(t.unit)
        : cellCenter(t.unit.col, t.unit.row);
    const d = worldDist(fromW, tw);
    const pri =
      t.scoreKind === "barrier"
        ? -1
        : card.priority.indexOf(t.scoreKind);
    // Barriers are path blockers: engage when in range, but after card priorities.
    const p = pri < 0 ? 50 : pri;
    if (p < bestPri || (p === bestPri && d < bestDist)) {
      best = t;
      bestPri = p;
      bestDist = d;
    }
  }
  return best;
}

function countSynergyAttackersOnEnemy(
  state: DropfrontState,
  enemy: EnemyAgent,
): number {
  let n = 0;
  for (const a of state.allies) {
    if (a.hp <= 0 || a.landing > 0 || !a.synergy) continue;
    const card = dropfrontCatalog.cardById[a.cardId];
    if (card.role === "barrier" || card.damage <= 0) continue;
    if (allyCanEngageEnemy(a, enemy, card.range)) n += 1;
  }
  return n;
}

function hasAdjacentAssaultCover(state: DropfrontState, piece: AllyPiece): boolean {
  for (const [dc, dr] of ORTHO) {
    const o = state.allies.find(
      (a) =>
        a.hp > 0 &&
        a.landing <= 0 &&
        a.col === piece.col + dc &&
        a.row === piece.row + dr &&
        dropfrontCatalog.cardById[a.cardId].role === "assault",
    );
    if (o) return true;
  }
  return false;
}

function hasAdjacentBarrierCover(state: DropfrontState, piece: AllyPiece): boolean {
  for (const [dc, dr] of ORTHO) {
    if (barrierAt(state, piece.col + dc, piece.row + dr)) return true;
  }
  return false;
}

function applyBarrierDamage(
  state: DropfrontState,
  b: BarrierToken,
  raw: number,
  events: SimEvent[],
  meta?: {
    role?: string;
    attackId?: string;
    x0?: number;
    y0?: number;
    x1?: number;
    y1?: number;
  },
): void {
  const dmg = Math.max(1, Math.round(raw));
  b.hp -= dmg;
  const c = cellCenter(b.col, b.row);
  events.push({
    type: DfEvent.Hit,
    id: b.id,
    toId: b.id,
    x: c.x,
    y: c.y,
    damage: dmg,
    role: meta?.role,
    targetKind: "barrier",
    attackId: meta?.attackId,
    x0: meta?.x0,
    y0: meta?.y0,
    x1: meta?.x1,
    y1: meta?.y1,
  });
  if (b.hp <= 0) {
    b.hp = 0;
    events.push({
      type: DfEvent.Destroy,
      id: b.id,
      x: c.x,
      y: c.y,
      kind: "barrier",
    });
  }
}

function inBeaconAura(state: DropfrontState, col: number, row: number): boolean {
  const range = dropfrontCatalog.combat.beaconAuraRange;
  for (const s of state.structures) {
    if (s.kind !== "beacon" || !s.alive || !s.active) continue;
    if (chebyshev(col, row, s.col, s.row) <= range) return true;
  }
  return false;
}

function allyNearStructure(state: DropfrontState, a: AllyPiece): boolean {
  for (const [dc, dr] of ORTHO) {
    const s = state.structures.find(
      (x) => x.alive && x.col === a.col + dc && x.row === a.row + dr,
    );
    if (s) return true;
  }
  return false;
}

function allyNearNest(state: DropfrontState, a: AllyPiece): boolean {
  for (const [dc, dr] of ORTHO) {
    const s = state.structures.find(
      (x) =>
        x.alive &&
        x.kind === "nest" &&
        x.col === a.col + dc &&
        x.row === a.row + dr,
    );
    if (s) return true;
  }
  return false;
}

function destroyStructure(
  state: DropfrontState,
  s: StructureToken,
  events: SimEvent[],
): void {
  s.alive = false;
  s.hp = 0;
  const c = cellCenter(s.col, s.row);
  events.push({
    type: DfEvent.Destroy,
    id: s.id,
    x: c.x,
    y: c.y,
    kind: s.kind,
  });
  const def = dropfrontCatalog.structureById[s.id];
  if (def) {
    events.push({ type: DfEvent.Toast, messageKey: def.destroyMessageKey });
  }
  if (s.kind === "hq") {
    state.phase = DfPhase.Won;
    events.push({ type: DfEvent.Won, messageKey: "df.msg.won" });
  } else if (s.kind === "depot") {
    const bonus = dropfrontCatalog.level.supplyDepotBonus;
    state.supply = Math.min(
      dropfrontCatalog.level.maxSupply,
      state.supply + bonus,
    );
    events.push({
      type: DfEvent.Supply,
      amount: bonus,
      messageKey: "df.msg.supplyGain",
    });
  } else if (s.kind === "beacon") {
    s.alive = true;
    s.hp = s.maxHp;
    s.active = true;
    events.push({ type: DfEvent.Beacon, messageKey: "df.msg.beaconOn" });
  }
}

function applyDamageToStructure(
  state: DropfrontState,
  s: StructureToken,
  raw: number,
  events: SimEvent[],
  meta?: {
    role?: string;
    attackId?: string;
    x0?: number;
    y0?: number;
    x1?: number;
    y1?: number;
  },
): void {
  const dmg = Math.max(1, Math.round(raw));
  s.hp -= dmg;
  const c = cellCenter(s.col, s.row);
  events.push({
    type: DfEvent.Hit,
    id: s.id,
    toId: s.id,
    x: c.x,
    y: c.y,
    damage: dmg,
    role: meta?.role,
    targetKind: "structure",
    attackId: meta?.attackId,
    x0: meta?.x0,
    y0: meta?.y0,
    x1: meta?.x1,
    y1: meta?.y1,
  });
  if (s.hp <= 0) destroyStructure(state, s, events);
}

function resolvePendingAttack(
  state: DropfrontState,
  atk: PendingAttack,
  events: SimEvent[],
): void {
  const meta = {
    role: atk.role,
    attackId: atk.id,
    x0: atk.x0,
    y0: atk.y0,
    x1: atk.x1,
    y1: atk.y1,
  };
  if (atk.targetKind === "enemy") {
    const enemy = state.enemies.find((e) => e.id === atk.toId && e.hp > 0);
    if (enemy) applyEnemyDamage(state, enemy, atk.damage, events, meta);
    return;
  }
  if (atk.targetKind === "ally") {
    const ally = state.allies.find((a) => a.id === atk.toId && a.hp > 0);
    if (ally) applyAllyDamage(state, ally, atk.damage, events, meta);
    return;
  }
  if (atk.targetKind === "barrier") {
    const b = state.barriers.find((x) => x.id === atk.toId && x.hp > 0);
    if (b) applyBarrierDamage(state, b, atk.damage, events, meta);
    return;
  }
  const s = state.structures.find((x) => x.id === atk.toId && x.alive);
  if (s) applyDamageToStructure(state, s, atk.damage, events, meta);
}

function tickAttacks(state: DropfrontState, dt: number, events: SimEvent[]): void {
  const next: PendingAttack[] = [];
  for (const atk of state.attacks) {
    atk.remain -= dt;
    if (atk.remain > 0) {
      next.push(atk);
      continue;
    }
    resolvePendingAttack(state, atk, events);
  }
  state.attacks = next;
  state.enemies = state.enemies.filter((e) => e.hp > 0);
  state.allies = state.allies.filter((a) => a.hp > 0);
  state.barriers = state.barriers.filter((b) => b.hp > 0);
}

function isOrthoAdjacent(
  c0: number,
  r0: number,
  c1: number,
  r1: number,
): boolean {
  return Math.abs(c0 - c1) + Math.abs(r0 - r1) === 1;
}

/** King-move adjacency (ortho + diagonal). */
function isChebyshevAdjacent(
  c0: number,
  r0: number,
  c1: number,
  r1: number,
): boolean {
  return chebyshev(c0, r0, c1, r1) === 1;
}

function allyCanEngageEnemy(
  a: AllyPiece,
  e: EnemyAgent,
  range: number,
): boolean {
  if (e.hp <= 0) return false;
  if (isChebyshevAdjacent(a.col, a.row, e.col, e.row)) return true;
  return worldDist(unitWorld(a), unitWorld(e)) <= range;
}

function allyCanEngageStructure(
  a: AllyPiece,
  s: StructureToken,
  range: number,
): boolean {
  if (!s.alive) return false;
  // Melee/short-range: allow diagonal stand-off (√2 > assault.range 1.25).
  if (isChebyshevAdjacent(a.col, a.row, s.col, s.row)) return true;
  return worldDist(unitWorld(a), cellCenter(s.col, s.row)) <= range;
}

function allyCanEngageBarrier(
  a: AllyPiece,
  b: BarrierToken,
  range: number,
): boolean {
  if (b.hp <= 0) return false;
  if (isChebyshevAdjacent(a.col, a.row, b.col, b.row)) return true;
  return worldDist(unitWorld(a), cellCenter(b.col, b.row)) <= range;
}

function nearestEnemy(
  state: DropfrontState,
  a: AllyPiece,
): EnemyAgent | null {
  let best: EnemyAgent | null = null;
  let bestD = Infinity;
  const aw = unitWorld(a);
  for (const e of state.enemies) {
    if (e.hp <= 0) continue;
    const d = worldDist(aw, unitWorld(e));
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

function allyMoveGoal(
  state: DropfrontState,
  a: AllyPiece,
): { col: number; row: number } | null {
  const card = dropfrontCatalog.cardById[a.cardId];
  if (card.role === "engineer") {
    const order: StructureKind[] = ["beacon", "depot", "turret", "nest", "hq"];
    for (const kind of order) {
      const s = state.structures.find(
        (x) => x.alive && x.kind === kind && !(kind === "beacon" && x.active),
      );
      if (!s) continue;
      const approach = bestApproachCell(
        state,
        s.col,
        s.row,
        a.col,
        a.row,
        { ignoreAllyId: a.id, mergeAllyCard: a.cardId },
      );
      if (approach) return approach;
    }
  }

  // Hold if any engageable enemy, structure, or barrier (stand-off / jam clear).
  for (const e of state.enemies) {
    if (allyCanEngageEnemy(a, e, card.range)) return null;
  }
  for (const s of state.structures) {
    if (allyCanEngageStructure(a, s, card.range)) return null;
  }
  for (const b of state.barriers) {
    if (allyCanEngageBarrier(a, b, card.range)) return null;
  }

  const best = nearestEnemy(state, a);
  if (best) {
    // Prefer empty ortho approach cell beside the enemy
    let approach: { col: number; row: number } | null = null;
    let approachD = Infinity;
    for (const [dc, dr] of ORTHO) {
      const nc = best.col + dc;
      const nr = best.row + dr;
      if (!inBounds(nc, nr)) continue;
      if (nc === a.col && nr === a.row) return null; // already flanking
      const mergeSame = state.allies.find(
        (o) =>
          o.hp > 0 &&
          o.id !== a.id &&
          o.col === nc &&
          o.row === nr &&
          o.cardId === a.cardId,
      );
      if (mergeSame) {
        const d = chebyshev(a.col, a.row, nc, nr);
        if (d < approachD) {
          approachD = d;
          approach = { col: nc, row: nr };
        }
        continue;
      }
      if (hardBlocked(state, nc, nr, { ignoreAllyId: a.id })) continue;
      const d = chebyshev(a.col, a.row, nc, nr);
      if (d < approachD) {
        approachD = d;
        approach = { col: nc, row: nr };
      }
    }
    if (approach) return approach;
    // No free flank: charge onto the enemy cell
    return { col: best.col, row: best.row };
  }

  const hq = state.structures.find((s) => s.kind === "hq" && s.alive);
  if (!hq) return null;
  return bestApproachCell(state, hq.col, hq.row, a.col, a.row, {
    ignoreAllyId: a.id,
    mergeAllyCard: a.cardId,
  });
}

function resolveAllyArriveCharge(
  state: DropfrontState,
  a: AllyPiece,
  events: SimEvent[],
): void {
  const en = state.enemies.find(
    (e) => e.hp > 0 && e.col === a.col && e.row === a.row,
  );
  if (!en) return;
  const card = dropfrontCatalog.cardById[a.cardId];
  const fromCol = a.fromCol;
  const fromRow = a.fromRow;
  setAim(a, aimAngleToward(unitWorld(a), unitWorld(en)));
  a.attackRemain = swingSecFor(a.cardId);
  applyEnemyDamage(state, en, card.damage * a.stackCount, events);
  if (en.hp > 0) {
    // Bounce back — enemy keeps the cell
    a.col = fromCol;
    a.row = fromRow;
  }
}

function tryMergeAllyMove(
  state: DropfrontState,
  mover: AllyPiece,
  col: number,
  row: number,
): boolean {
  const other = state.allies.find(
    (a) =>
      a.hp > 0 &&
      a.id !== mover.id &&
      a.col === col &&
      a.row === row &&
      a.cardId === mover.cardId,
  );
  if (!other) return false;
  mergeAllyInto(other, mover.stackCount, mover.hp);
  mover.hp = 0;
  mover.stackCount = 0;
  return true;
}

/** Nearest combat focus used to decide which adjacent stack is "forward". */
function allyFocusCell(
  state: DropfrontState,
  a: AllyPiece,
): { col: number; row: number } | null {
  const card = dropfrontCatalog.cardById[a.cardId];
  if (card.role === "engineer") {
    const order: StructureKind[] = ["beacon", "depot", "turret", "nest", "hq"];
    for (const kind of order) {
      const s = state.structures.find(
        (x) => x.alive && x.kind === kind && !(kind === "beacon" && x.active),
      );
      if (s) return { col: s.col, row: s.row };
    }
  }
  let best: EnemyAgent | null = null;
  let bestD = Infinity;
  for (const e of state.enemies) {
    if (e.hp <= 0) continue;
    const d = chebyshev(a.col, a.row, e.col, e.row);
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  if (best) return { col: best.col, row: best.row };
  const hq = state.structures.find((s) => s.kind === "hq" && s.alive);
  return hq ? { col: hq.col, row: hq.row } : null;
}

/**
 * When blocked / holding in range: merge into an adjacent same-cardId stack
 * that is strictly closer to the threat (rear queue → front stack).
 */
function tryForwardMerge(state: DropfrontState, a: AllyPiece): boolean {
  const focus = allyFocusCell(state, a);
  if (!focus) return false;
  const myD = chebyshev(a.col, a.row, focus.col, focus.row);
  const max = dropfrontCatalog.combat.maxStack;
  let best: AllyPiece | null = null;
  let bestD = myD;
  for (const [dc, dr] of ORTHO) {
    const other = allyAt(state, a.col + dc, a.row + dr);
    if (!other || other.id === a.id) continue;
    if (other.cardId !== a.cardId) continue;
    if (other.landing > 0) continue;
    if (other.stackCount >= max) continue;
    const d = chebyshev(other.col, other.row, focus.col, focus.row);
    if (d < bestD) {
      bestD = d;
      best = other;
    }
  }
  if (!best) return false;
  mergeAllyInto(best, a.stackCount, a.hp);
  a.hp = 0;
  a.stackCount = 0;
  return true;
}

function stepAllies(state: DropfrontState, dt: number, events: SimEvent[]): void {
  const interval = Math.max(0.05, dropfrontCatalog.combat.allyMoveInterval);
  for (const a of state.allies) {
    if (a.hp <= 0 || a.landing > 0) continue;
    const card = dropfrontCatalog.cardById[a.cardId];
    if (!card.canMove) continue;

    a.attackRemain = Math.max(0, a.attackRemain - dt);
    // Attack lock: stand still (no step progress / new steps).
    if (isAttacking(a)) continue;

    // Advance in-progress step; combat uses unitWorld during lerp.
    if (isMoving(a)) {
      a.moveT = Math.min(1, a.moveT + dt / interval);
      if (a.moveT < 1) continue;
      if (tryMergeAllyMove(state, a, a.col, a.row)) {
        continue;
      }
      resolveAllyArriveCharge(state, a, events);
      snapIdle(a);
      if (a.hp <= 0) continue;
    }

    const goal = allyMoveGoal(state, a);
    let advanced = false;
    if (goal) {
      const step = nextStepToward(state, a.col, a.row, goal.col, goal.row, {
        ignoreAllyId: a.id,
        mergeAllyCard: a.cardId,
        chargeEnemyGoal: true,
      });
      if (step) {
        const struct = state.structures.find(
          (s) => s.alive && s.col === step.col && s.row === step.row,
        );
        if (struct) {
          // Don't enter structure cells — stand off
        } else {
          const mergeTarget = state.allies.find(
            (o) =>
              o.hp > 0 &&
              o.id !== a.id &&
              o.col === step.col &&
              o.row === step.row &&
              o.cardId === a.cardId,
          );
          const en = enemyAt(state, step.col, step.row);
          if (mergeTarget || en) {
            setAim(a, Math.atan2(step.row - a.row, step.col - a.col));
            beginStep(a, step.col, step.row);
            advanced = true;
          } else if (!hardBlocked(state, step.col, step.row, { ignoreAllyId: a.id })) {
            setAim(a, Math.atan2(step.row - a.row, step.col - a.col));
            beginStep(a, step.col, step.row);
            advanced = true;
          }
        }
      }
    }
    if (!advanced && a.hp > 0 && !isMoving(a)) {
      tryForwardMerge(state, a);
    }
  }
  state.allies = state.allies.filter((a) => a.hp > 0);
  state.enemies = state.enemies.filter((e) => e.hp > 0);
}

function tryMergeEnemyArrive(state: DropfrontState, e: EnemyAgent): boolean {
  const other = state.enemies.find(
    (o) =>
      o.hp > 0 &&
      o.id !== e.id &&
      o.col === e.col &&
      o.row === e.row &&
      o.role === e.role,
  );
  if (!other) return false;
  const max = dropfrontCatalog.combat.maxStack;
  const room = Math.max(0, max - other.stackCount);
  const n = Math.min(e.stackCount, room);
  if (n > 0) {
    other.stackCount += n;
    other.hp += e.maxHpPerUnit * n;
  }
  e.hp = 0;
  e.stackCount = 0;
  return true;
}

function stepEnemies(state: DropfrontState, dt: number, events: SimEvent[]): void {
  for (const e of state.enemies) {
    if (e.hp <= 0) continue;
    const def = dropfrontCatalog.combat.enemyRoles[e.role];
    const interval = Math.max(0.05, def.moveInterval);
    const range = def.range > 0 ? def.range : MELEE_RANGE;
    e.engaging = false;
    e.attackRemain = Math.max(0, e.attackRemain - dt);
    e.fireCd = Math.max(0, e.fireCd - dt);

    const ew = unitWorld(e);

    const tryEnqueue = (
      toId: string,
      to: { x: number; y: number },
      damage: number,
      targetKind: AttackTargetKind,
    ): void => {
      const swing = swingSecFor("swarm");
      setAim(e, aimAngleToward(ew, to));
      const { lockSec } = enqueueAttack(state, events, {
        fromId: e.id,
        toId,
        role: e.role === "gunner" ? "shooter" : "swarm",
        from: ew,
        to,
        damage,
        targetKind,
        swingSec: swing,
      });
      e.attackRemain = lockSec;
      e.fireCd = swing;
      e.engaging = true;
    };

    if (!isAttacking(e) && e.fireCd <= 0) {
      // 1) Ally in range
      let bestAlly: AllyPiece | null = null;
      let bestAllyD = Infinity;
      for (const a of state.allies) {
        if (a.hp <= 0 || a.landing > 0) continue;
        const d = worldDist(ew, unitWorld(a));
        const ok =
          isOrthoAdjacent(e.col, e.row, a.col, a.row) || d <= range;
        if (!ok) continue;
        if (d < bestAllyD) {
          bestAllyD = d;
          bestAlly = a;
        }
      }
      if (bestAlly) {
        const swing = swingSecFor("swarm");
        tryEnqueue(
          bestAlly.id,
          unitWorld(bestAlly),
          e.dps * e.stackCount * swing,
          "ally",
        );
      } else {
        // Barriers only — never attack own nest/hq/turret/depot/beacon.
        let adjBarrier: BarrierToken | null = null;
        for (const [dc, dr] of ORTHO) {
          const b = barrierAt(state, e.col + dc, e.row + dr);
          if (b) {
            adjBarrier = b;
            break;
          }
        }
        if (adjBarrier) {
          const swing = swingSecFor("swarm");
          tryEnqueue(
            adjBarrier.id,
            cellCenter(adjBarrier.col, adjBarrier.row),
            e.dps * e.stackCount * swing * def.structureMul,
            "barrier",
          );
        }
      }
    }

    if (isAttacking(e) || e.engaging) continue;

    if (isMoving(e)) {
      e.moveT = Math.min(1, e.moveT + dt / interval);
      if (e.moveT < 1) continue;
      snapIdle(e);
      tryMergeEnemyArrive(state, e);
      if (e.hp <= 0) continue;
    }

    // Goal selection
    let goalCol = 0;
    let goalRow = 0;
    let bestD = Infinity;
    let found = false;

    // Brute: prefer player barriers, then allies (never own structures).
    if (e.role === "brute") {
      for (const b of state.barriers) {
        if (b.hp <= 0) continue;
        const d = chebyshev(e.col, e.row, b.col, b.row);
        if (d < bestD) {
          bestD = d;
          goalCol = b.col;
          goalRow = b.row;
          found = true;
        }
      }
    }

    if (!found) {
      bestD = Infinity;
      for (const a of state.allies) {
        if (a.hp <= 0 || a.landing > 0) continue;
        const d = worldDist(unitWorld(e), unitWorld(a));
        if (d < bestD) {
          bestD = d;
          goalCol = a.col;
          goalRow = a.row;
          found = true;
        }
      }
    }

    if (!found) {
      // March toward player drop band (bottom), not own HQ.
      goalCol = Math.floor(dropfrontCatalog.level.cols / 2);
      goalRow = dropfrontCatalog.level.rows - 2;
    }

    // Gunner hold if ally already in range
    if (e.role === "gunner") {
      let inRange = false;
      for (const a of state.allies) {
        if (a.hp <= 0 || a.landing > 0) continue;
        if (worldDist(ew, unitWorld(a)) <= range) {
          inRange = true;
          break;
        }
      }
      if (inRange) continue;
    }

    let advanced = false;
    const stepOpts = {
      ignoreEnemyId: e.id,
      mergeEnemyRole: e.role,
    };
    let step = nextStepToward(state, e.col, e.row, goalCol, goalRow, stepOpts);

    // Runner: if step is blocked by barrier adjacency preference — try detour
    if (e.role === "runner" && step) {
      const ontoBarrier = barrierAt(state, step.col, step.row);
      if (ontoBarrier) step = null;
    }
    if (e.role === "runner" && !step) {
      let best: { col: number; row: number } | null = null;
      let bestFocus = chebyshev(e.col, e.row, goalCol, goalRow);
      for (const [dc, dr] of ORTHO) {
        const nc = e.col + dc;
        const nr = e.row + dr;
        if (barrierAt(state, nc, nr)) continue;
        if (hardBlocked(state, nc, nr, { ignoreEnemyId: e.id })) continue;
        const d = chebyshev(nc, nr, goalCol, goalRow);
        if (d < bestFocus) {
          bestFocus = d;
          best = { col: nc, row: nr };
        }
      }
      step = best;
    }

    if (step) {
      const other = state.enemies.find(
        (o) =>
          o.hp > 0 &&
          o.id !== e.id &&
          o.col === step!.col &&
          o.row === step!.row &&
          o.role === e.role,
      );
      if (other) {
        setAim(e, Math.atan2(step.row - e.row, step.col - e.col));
        beginStep(e, step.col, step.row);
        advanced = true;
      } else if (
        !allyAt(state, step.col, step.row) &&
        !hardBlocked(state, step.col, step.row, { ignoreEnemyId: e.id })
      ) {
        setAim(e, Math.atan2(step.row - e.row, step.col - e.col));
        beginStep(e, step.col, step.row);
        advanced = true;
      }
    }

    if (!advanced && e.hp > 0 && !isMoving(e)) {
      const max = dropfrontCatalog.combat.maxStack;
      let best: EnemyAgent | null = null;
      let bestFocus = chebyshev(e.col, e.row, goalCol, goalRow);
      for (const [dc, dr] of ORTHO) {
        const other = enemyAt(state, e.col + dc, e.row + dr);
        if (!other || other.id === e.id || other.role !== e.role) continue;
        if (other.stackCount >= max) continue;
        const d = chebyshev(other.col, other.row, goalCol, goalRow);
        if (d < bestFocus) {
          bestFocus = d;
          best = other;
        }
      }
      if (best) {
        const room = Math.max(0, max - best.stackCount);
        const n = Math.min(e.stackCount, room);
        if (n > 0) {
          best.stackCount += n;
          best.hp += e.maxHpPerUnit * n;
        }
        e.hp = 0;
        e.stackCount = 0;
      }
    }
  }
  state.enemies = state.enemies.filter((x) => x.hp > 0);
  state.allies = state.allies.filter((a) => a.hp > 0);
  state.barriers = state.barriers.filter((b) => b.hp > 0);
}

export function tickDropfront(state: DropfrontState, dtRaw: number): DropfrontState {
  const events: SimEvent[] = [];
  state.events = events;
  const fx = dropfrontCatalog.view.fx;
  const combat = dropfrontCatalog.combat;
  const level = dropfrontCatalog.level;
  const dt = Math.min(fx.maxDt, Math.max(0, dtRaw));
  if (dt <= 0 || state.phase === DfPhase.Paused) return state;
  if (state.phase !== DfPhase.Playing) return state;

  state.time += dt;
  state.supply = Math.min(
    level.maxSupply,
    state.supply + level.supplyRegenPerSec * dt,
  );

  for (const a of state.allies) {
    if (a.hp <= 0 || a.landing <= 0) continue;
    a.landing = Math.max(0, a.landing - dt);
    if (a.landing <= 0) {
      const c = cellCenter(a.col, a.row);
      events.push({ type: DfEvent.Land, id: a.id, x: c.x, y: c.y });
    }
  }

  refreshSynergy(state);

  for (const a of state.allies) {
    if (a.hp <= 0 || a.landing > 0 || !a.synergy) continue;
    a.hp = Math.min(
      a.maxHpPerUnit * a.stackCount,
      a.hp + combat.synergyRegenPerSec * dt,
    );
  }

  stepAllies(state, dt, events);

  // Combat — queue delayed attacks (damage on impact)
  for (const a of state.allies) {
    if (a.hp <= 0 || a.landing > 0) continue;
    const card = dropfrontCatalog.cardById[a.cardId];
    if (card.role === "barrier" || card.damage <= 0) continue;
    a.fireCd = Math.max(0, a.fireCd - dt);
    if (isMoving(a) || isAttacking(a)) continue;
    const target = pickTarget(card, a, state);
    if (!target || a.fireCd > 0) continue;
    let fireInterval = card.fireInterval;
    if (card.role === "engineer" && allyNearStructure(state, a)) {
      fireInterval *= combat.engineerNearStructFireMul;
    }
    if (inBeaconAura(state, a.col, a.row)) {
      fireInterval *= combat.beaconFireMul;
    }
    a.fireCd = fireInterval;
    const from = unitWorld(a);
    const to =
      target.kind === "enemy"
        ? unitWorld(target.unit)
        : cellCenter(target.unit.col, target.unit.row);
    setAim(a, aimAngleToward(from, to));
    const swing = swingSecFor(a.cardId);
    let dmg = card.damage * a.stackCount;
    if (target.kind === "enemy") {
      const near =
        chebyshev(a.col, a.row, target.unit.col, target.unit.row) <= 1;
      if (card.role === "assault" && near) dmg *= combat.assaultMeleeMul;
      if (card.role === "shooter" && near) dmg *= combat.shooterMeleeMul;
    } else {
      dmg *= card.structureMul;
    }
    const targetKind: AttackTargetKind =
      target.kind === "enemy"
        ? "enemy"
        : target.kind === "barrier"
          ? "barrier"
          : "structure";
    const { lockSec } = enqueueAttack(state, events, {
      fromId: a.id,
      toId: target.unit.id,
      role: a.cardId,
      from,
      to,
      damage: dmg,
      targetKind,
      swingSec: swing,
    });
    a.attackRemain = lockSec;
  }

  // Engineer nest slow
  for (const a of state.allies) {
    if (a.hp <= 0 || a.landing > 0) continue;
    if (dropfrontCatalog.cardById[a.cardId].role !== "engineer") continue;
    if (allyNearNest(state, a)) {
      state.nestSpawnCd += combat.engineerNestSlowPerSec * dt;
    }
  }

  // Depot passive supply
  const depot = state.structures.find((s) => s.kind === "depot" && s.alive);
  if (depot) {
    state.supply = Math.min(
      level.maxSupply,
      state.supply + combat.depotRegenPerSec * dt,
    );
    if (level.depotEnemyAura) {
      for (const e of state.enemies) {
        if (e.hp <= 0) continue;
        if (chebyshev(e.col, e.row, depot.col, depot.row) > 2) continue;
        e.hp = Math.min(
          e.maxHpPerUnit * e.stackCount,
          e.hp + combat.depotEnemyAuraRegen * dt,
        );
      }
    }
  }

  // HQ pulse (late levels)
  if (level.hqPulse) {
    state.hqPulseCd -= dt;
    if (state.hqPulseCd <= 0) {
      state.hqPulseCd = combat.hqPulseInterval;
      const hq = state.structures.find((s) => s.kind === "hq" && s.alive);
      if (hq) {
        for (const a of state.allies) {
          if (a.hp <= 0 || a.landing > 0) continue;
          if (chebyshev(a.col, a.row, hq.col, hq.row) > combat.hqPulseRange) {
            continue;
          }
          applyAllyDamage(state, a, combat.hqPulseDamage, events, {
            role: "hq",
          });
        }
      }
    }
  }

  // Enemy turrets
  for (const s of state.structures) {
    if (!s.alive || s.kind !== "turret") continue;
    s.fireCd = Math.max(0, s.fireCd - dt);
    if (s.fireCd > 0) continue;
    const sw = cellCenter(s.col, s.row);
    let best: AllyPiece | null = null;
    let bestD = Infinity;
    for (const a of state.allies) {
      if (a.hp <= 0 || a.landing > 0) continue;
      const d = worldDist(sw, unitWorld(a));
      if (d > combat.turretRange) continue;
      if (d < bestD) {
        bestD = d;
        best = a;
      }
    }
    if (!best) continue;
    s.fireCd = combat.turretFireInterval;
    const to = unitWorld(best);
    enqueueAttack(state, events, {
      fromId: s.id,
      toId: best.id,
      role: "shooter",
      from: sw,
      to,
      damage: combat.turretDps * combat.turretFireInterval,
      targetKind: "ally",
      swingSec: combat.turretFireInterval,
    });
  }

  stepEnemies(state, dt, events);
  tickAttacks(state, dt, events);

  const nests = state.structures.filter((s) => s.kind === "nest" && s.alive);
  if (nests.length > 0) {
    state.nestSpawnCd -= dt;
    if (state.nestSpawnCd <= 0) {
      state.nestSpawnCd = level.nestSpawnInterval;
      const roles = level.nestSpawnRoles;
      for (const nest of nests) {
        for (let i = 0; i < level.nestSpawnCount; i++) {
          const role = roles[(state.nextId + i) % roles.length]!;
          let placed = false;
          for (const [dc, dr] of ORTHO) {
            const nc = nest.col + dc;
            const nr = nest.row + dr;
            if (!inBounds(nc, nr)) continue;
            const same = enemyAt(state, nc, nr);
            if (same && same.role === role) {
              const max = combat.maxStack;
              if (same.stackCount < max) {
                same.stackCount += 1;
                same.hp += same.maxHpPerUnit;
                placed = true;
                break;
              }
            }
            if (hardBlocked(state, nc, nr)) continue;
            state.enemies.push(spawnEnemy(state, role, nc, nr));
            placed = true;
            break;
          }
          if (!placed) {
            // Never spawn on the nest cell (stood on hatchery looked like self-damage).
            break;
          }
        }
      }
    }
  }

  state.barriers = state.barriers.filter((b) => b.hp > 0);

  refreshSynergy(state);

  const cheapest = Math.min(...dropfrontCatalog.cards.map((c) => c.cost));
  if (
    state.phase === DfPhase.Playing &&
    state.allies.length === 0 &&
    state.supply < cheapest
  ) {
    state.phase = DfPhase.Lost;
    events.push({ type: DfEvent.Lost, messageKey: "df.msg.lost" });
  }

  return state;
}

export function getHudSnapshot(state: DropfrontState): HudSnapshot {
  const hq = state.structures.find((s) => s.kind === "hq");
  const cardAfford = {} as Record<DropfrontCardId, boolean>;
  for (const c of dropfrontCatalog.cards) {
    cardAfford[c.id] = state.supply >= c.cost;
  }
  return {
    supply: Math.floor(state.supply),
    hqHp: hq?.hp ?? 0,
    hqMax: hq?.maxHp ?? 1,
    phase: state.phase,
    selectedCard: state.selectedCard,
    cardAfford,
  };
}
